import { Injectable } from "@angular/core";
import { ServiceMessageBrokerFactory } from "shared/provider-webworker";

import { PrimaryLookupService } from "../../impl/PrimaryLookupService";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BenchmarkFixingLookupService } from "./BenchmarkFixingLookupService";
import { CurrencyPairGroupPromiseLookupService } from "./CurrencyPairGroupPromiseLookupService";

const PAIR_WILDCARD: string = "***";
const BRANCH_WILDCARD: string = null;
const FIXING_TIME_LEN: number = 4;
const FIXING_TIME_HOUR_LEN: number = 2;

export class BenchmarkFixingTime {
  localTime: CubiqTS.Long = null;
  timezoneCd: string;
  label: string;
  value: string;
  constructor(localTime: CubiqTS.Long, timezoneCd: string) {
    this.localTime = localTime;
    this.timezoneCd = timezoneCd;
    let strTime = this.localTime.toString();

    // pad if necessary
    if (strTime.length < FIXING_TIME_LEN) {
      strTime = strTime.padStart(FIXING_TIME_LEN, "0");
    }

    let hour: string = strTime.substring(0, FIXING_TIME_HOUR_LEN);
    let minute: string = strTime.substring(FIXING_TIME_HOUR_LEN, FIXING_TIME_LEN);

    this.label = hour + ":" + minute + " " + this.timezoneCd;
    this.value = hour + minute;
  }
}

export class BenchmarkFixingDetail {
  code: string;
  pair: string; // base + nonbase
  branch: string;
  times: BenchmarkFixingTime[] = [];

  constructor(code: string, pair: string, branch: string = null) {
    this.code = code;
    this.pair = pair;
    this.branch = branch;
  }

  public static asKey(code: string, pair: string, branch: string): string {
    let ret: string = code + pair + (branch == null ? "" : branch);
    return ret;
  }
}

@Injectable()
export class BenchmarkFixingPrimaryLookupService extends PrimaryLookupService<
  string,
  CubiqTS.BenchmarkFixingImpl
> {
  // index of BMCODEPAIR x Benchmark Detail
  protected detailIdx: Map<string, BenchmarkFixingDetail> = new Map();

  protected codeIdx: Map<string, Set<BenchmarkFixingDetail>> = new Map();
  bmFixingSub: any;
  pairGrpSub: any;

  LOG_PREFIX: string = "[BenchmarkFixingReplicaLookupService] ";
  TRACE_LOGGING: boolean = true;

  private traceLog(logString: string) {
    if (this.TRACE_LOGGING) logger.out.info(this.LOG_PREFIX + logString);
  }
  constructor(
    protected base: BenchmarkFixingLookupService,
    protected pairGroupSvc: CurrencyPairGroupPromiseLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
  ) {
    super(base, factory, CubiqTS.BenchmarkFixingImpl, cubiqWorker, "BenchmarkFixingImpl");

    Promise.all([cubiqWorker.isAuth(`${this.constructor.name}`), pairGroupSvc.isPrimed()]).then(
      () => {
        let bmFixObservable = cubiqWorker.primeStaticData<CubiqTS.BenchmarkFixingImpl>(
          CubiqTS.BenchmarkFixingImpl.name,
        );

        bmFixObservable.subscribe(
          (bmFixes: CubiqTS.BenchmarkFixingImpl[]) => {
            this.base.prime(bmFixes);
          },
          (error: Error) => {
            console.debug("BenchmarkFixing prime failed");
            this._primed.reject(error);
          },
          () => {
            this._primed.resolve(true);
          },
        );
      },
      (reason) => {
        console.debug("BenchmarkFixingPrimaryLookupService: Failed to prime: " + reason);

        this._primed.resolve(false);
      },
    );

    this.indexData();
    this.registerForDataChanges();

    //        this.broker.registerMethod('getBenchmarkFixing', [PRIMITIVE], this.getBenchmarkFixing.bind(this), this.VALUE);
  }

  private registerForDataChanges() {
    this.bmFixingSub = this.getModelUpdateSubject().subscribe(this.onDataUpdated.bind(this));
    this.pairGrpSub = this.pairGroupSvc
      .getModelUpdateSubject()
      .subscribe(this.onDataUpdated.bind(this));
  }

  ngOnDestroy() {
    if (this.bmFixingSub) this.bmFixingSub.unsubscribe();
    if (this.pairGrpSub) this.pairGrpSub.unsubscribe();
  }

  protected onDataUpdated(modelEvent: Array<any>): void {
    this.detailIdx = new Map();
    this.codeIdx = new Map();
    this.indexData();
  }

  indexed: boolean = false;
  private indexData(force: boolean = true): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (!force && this.indexed) {
        resolve(this.indexed);
        return;
      }

      this.isPrimed().then((primed) => {
        this.pairGroupSvc.isPrimed().then((pairGroupsprimed) => {
          this.getAll().then((found) => {
            for (let index = 0; index < found.length; index++) {
              const fixing: CubiqTS.BenchmarkFixingImpl = found[index];
              this.indexItem(fixing);
            }
            this.indexed = true;
            resolve(this.indexed);
          });
        });
      });
    });
  }

  private indexItem(item: CubiqTS.BenchmarkFixingImpl): void {
    /*
          we want 
            [codeXpair] x {fixing x {[time & tz]} }
          with an additional index  of 
            [codeXpairXbranch] to same fixing

          and we want
            [pairXbranch] x {bmCode}

          so we can find the BM codes for a pair and branch

          and given a pair and code we can find the detail - which includes the fixing times

      */
    if (item.rules)
      item.rules.map((rule: CubiqTS.BenchmarkFixingRule) => {
        if (rule.currencyPairGroupId) {
          this.pairGroupSvc.isPrimed().then((isPrimed) => {
            let pairs: string[] = this.pairGroupSvc.getPairs(rule.currencyPairGroupId.toNumber());
            if (pairs)
              pairs.forEach((pair) => {
                let code: string = rule.benchmarkCd;

                if (code == null) {
                  code = item.benchmarkCd;
                }

                let branchCd = rule.branchCd;
                // see if we have fixing for code x pair x branch
                // if not,  create one - put it in the index -
                let foundFixing: BenchmarkFixingDetail = this.detailIndex(code, pair, branchCd);
                foundFixing.times.push(new BenchmarkFixingTime(rule.localTime, rule.timezoneCd));

                // now add it to the code index
                // Map<string, Set<BenchmarkFixingDetail>>
                let pairBranchKey: string = pair + "" + (branchCd ? branchCd : "");
                this.codeIndex(pairBranchKey, foundFixing);
              });
          });
        }
      });
  }

  private detailIndex(code: string, pair: string, branchCd: string): BenchmarkFixingDetail {
    let key: string = BenchmarkFixingDetail.asKey(code, pair, branchCd);

    let foundFixing: BenchmarkFixingDetail = this.detailIdx.get(key);
    if (null == foundFixing) {
      foundFixing = new BenchmarkFixingDetail(code, pair, branchCd);
      this.detailIdx.set(key, foundFixing);
    }
    return foundFixing;
  }

  private codeIndex(key: string, fixing: BenchmarkFixingDetail) {
    let foundBykey: Set<BenchmarkFixingDetail> = this.codeIdx.get(key);
    if (!foundBykey) {
      foundBykey = new Set<BenchmarkFixingDetail>();
      this.codeIdx.set(key, foundBykey);
    }
    foundBykey.add(fixing);
  }
  /**
   * Here we wnat to look up exact matches (pairxbranch) - and also branch wildcards (pair)
   * Then blend the results - so we only have one "Detail" with all the relevant times in it.
   * @param pair
   * @param branch
   */
  public getBenchmarkCodesForPairAndBranch(
    base: string,
    nonBase: string,
    branch: string,
  ): Promise<Map<string, BenchmarkFixingDetail>> {
    return new Promise((resolve) => {
      this.indexData(false).then(() => {
        let ret: Map<string, BenchmarkFixingDetail> = new Map<string, BenchmarkFixingDetail>();
        if (base && nonBase) {
          // need to find all combinations of the :
          // pair + branch
          // pair + branch wildcard
          // base*** + branch
          // base*** + branch wildcard
          // nonbase*** + branch
          // nonbase*** + branch wildcard

          this.getBMCForPairAndBranch(ret, base, nonBase, branch);
          this.getBMCForPairAndBranch(ret, base, PAIR_WILDCARD, branch);
          this.getBMCForPairAndBranch(ret, nonBase, PAIR_WILDCARD, branch);

          //            if(union && union.size > 0) ret = new Set(union.values());
        }
        resolve(ret);
      });
    });
  }

  private getBMCForPairAndBranch(
    union: Map<string, BenchmarkFixingDetail>,
    base: string,
    nonBase: string,
    branch: string,
  ) {
    let pairBranchKey: string = base + "" + nonBase + "" + branch;
    let found: Set<BenchmarkFixingDetail> = this.codeIdx.get(pairBranchKey);

    let pair: string = base + "" + nonBase;
    let branchWildcards: Set<BenchmarkFixingDetail> = this.codeIdx.get(pair);

    // lose any dups
    // need to blend the times for the same  code into each other - so code only appears once.
    if (found && found.size > 0) {
      found.forEach(function (value: BenchmarkFixingDetail) {
        this.addDetailToUnion(value, union);
      }, this);
    }
    if (branchWildcards && branchWildcards.size > 0) {
      branchWildcards.forEach(function (value: BenchmarkFixingDetail) {
        this.addDetailToUnion(value, union);
      }, this);
    }
  }

  private addDetailToUnion(
    value: BenchmarkFixingDetail,
    union: Map<string, BenchmarkFixingDetail>,
  ) {
    let detail: BenchmarkFixingDetail = union.get(value.code);
    if (!detail) {
      detail = new BenchmarkFixingDetail(value.code, value.pair);
      union.set(detail.code, detail);
    }

    // need to make sure we only add the time, if it is already in there.
    value.times.forEach((val) => {
      detail.times.push(val);
    });
    let fixingTimesArray = detail.times;
    detail.times = [
      ...new Map(fixingTimesArray.map((fixingTime) => [fixingTime.label, fixingTime])).values(),
    ];
  }

  public getBenchmarkDetail(
    code: string,
    base: string,
    nonBase: string,
    branch: string,
  ): Promise<BenchmarkFixingDetail | null> {
    this.traceLog(
      "getBenchmarkDetail [" + code + "][" + base + "][" + nonBase + "][" + branch + "]",
    );
    return new Promise((resolve) => {
      this.indexData(false).then(() => {
        this.getBenchmarkCodesForPairAndBranch(base, nonBase, branch).then(
          (codeDetails: Map<string, BenchmarkFixingDetail>) => {
            if (codeDetails instanceof Map) resolve(codeDetails.get(code));
            else resolve(null);
          },
        );
      });
    });
  }
}
